/**
 * AVS-4438 (D2): treat a talking dealer as active.
 *
 * The inactivity agent in `./timeout.ts` is reset only by raw DOM events on `document`.
 * A dealer who is mid-consultation but not touching mouse or keyboard therefore looks
 * idle, and the 20-minute timer runs down under them.
 *
 * Why audio LEVEL and not "is a peer connected" or "are packets flowing": a forgotten,
 * abandoned call still holds a peer connection and still sends media. Keying on either of
 * those would silently turn the inactivity timeout into "never fires while a call is open",
 * which is exactly the reaping behaviour the feature exists to provide. An audio level
 * above the noise floor is the only cheap signal that separates "someone is talking" from
 * "someone walked away with the tab open".
 *
 * The stats source is injected rather than reached for, so the decision logic is testable
 * without a live `RTCPeerConnection`.
 */

/**
 * The subset of `RTCStats` this module reads. Declared narrowly (rather than leaning on
 * `RTCStatsReport`, whose values are `any` in the DOM lib) so the module stays `any`-free.
 */
export interface IAudioLevelStat {
  type: string;
  kind?: string;
  audioLevel?: number;
}

/**
 * Audio levels are normalised 0..1. Room tone and mic self-noise sit well below 0.01 on the
 * hardware seen in the 2026-09-09 field test; ordinary speech sits an order of magnitude
 * higher. 0.02 clears the noise floor without demanding that the dealer raise their voice.
 */
export const SPEECH_AUDIO_LEVEL_THRESHOLD = 0.02;

/**
 * How often to sample. The timeout it defends is measured in minutes, so sampling every few
 * seconds is ample; polling faster would burn battery for no behavioural gain.
 */
export const SPEECH_POLL_INTERVAL_MS = 5_000;

/**
 * True when the LOCAL microphone carries speech.
 *
 * Only `media-source` entries of kind `audio` are considered: that is this client's own
 * capture. `inbound-rtp` also exposes `audioLevel`, but a remote participant talking says
 * nothing about whether THIS dealer is still at their desk, and counting it would let a
 * chatty customer hold a dealer's abandoned session open indefinitely.
 */
export function isSpeaking(
  stats: Iterable<IAudioLevelStat>,
  threshold: number = SPEECH_AUDIO_LEVEL_THRESHOLD
): boolean {
  for (const stat of stats) {
    if (stat.type !== 'media-source' || stat.kind !== 'audio') continue;
    if ((stat.audioLevel ?? 0) > threshold) return true;
  }
  return false;
}

/**
 * Poll `getStats` and call `onSpeech` whenever the local microphone carries speech.
 *
 * Returns a stop function. A rejected or unavailable stats read is a non-event: the dealer
 * simply is not credited with activity for that tick, and the timer keeps running down —
 * failing closed, so a broken probe can never keep a dead session alive.
 */
export function startSpeechActivityMonitor(
  getStats: () => Promise<Iterable<IAudioLevelStat>>,
  onSpeech: () => void,
  intervalMs: number = SPEECH_POLL_INTERVAL_MS
): () => void {
  let stopped = false;

  const sample = async (): Promise<void> => {
    const stats = await getStats().catch(() => null);
    if (stopped || !stats) return;
    if (isSpeaking(stats)) onSpeech();
  };

  // `sample` only catches the getStats read. `onSpeech` reaches a userSession emit, and a
  // listener that throws would otherwise reject this promise every tick with nobody to catch it.
  const intervalId = setInterval(() => {
    sample().catch(() => {});
  }, intervalMs);

  return () => {
    stopped = true;
    clearInterval(intervalId);
  };
}

/**
 * Narrow an `RTCStatsReport` down to the fields this module reads.
 *
 * `RTCStatsReport`'s values are `any` in the DOM lib, so every field is re-checked here and the
 * result is a plain typed array — that is what keeps the module free of `any`.
 */
export function toAudioLevelStats(report: ReadonlyMap<string, unknown>): IAudioLevelStat[] {
  const stats: IAudioLevelStat[] = [];

  report.forEach((value) => {
    if (typeof value !== 'object' || value === null) return;
    const entry = value as Record<string, unknown>;
    if (typeof entry.type !== 'string') return;

    stats.push({
      type: entry.type,
      kind: typeof entry.kind === 'string' ? entry.kind : undefined,
      audioLevel: typeof entry.audioLevel === 'number' ? entry.audioLevel : undefined,
    });
  });

  return stats;
}

/**
 * Read this client's own microphone level off any live peer connection.
 *
 * `media-source` is a per-sender stat, so one read on any one connection answers the question:
 * there is no need to walk every peer.
 *
 * The import is deferred for TEST ISOLATION, not to break a cycle. `utils/ui/voipStats` is already
 * in this module's static graph (`state/session` imports `utils/ui/rtc`, which imports
 * `./voipStats`), so a static import here would add no new edge. Deferring it keeps the webrtc
 * graph out of the timeout unit tests, which mock `utils/ui/rtc` wholesale.
 */
async function readLocalAudioStats(): Promise<Iterable<IAudioLevelStat>> {
  const { readLiveVoipPeers } = await import('utils/ui/voipStats');
  const peerConnection = readLiveVoipPeers()?.find((peer) => !peer.transportFailed)?.peerConnection;
  if (!peerConnection) return [];

  const report = await peerConnection.getStats();
  return toAudioLevelStats(report);
}

/**
 * AVS-4438 (D2): keep a talking dealer's session alive.
 *
 * Wraps {@link startSpeechActivityMonitor} with the real stats source. Returns a stop function.
 */
export function startLocalSpeechMonitor(
  onSpeech: () => void,
  intervalMs: number = SPEECH_POLL_INTERVAL_MS
): () => void {
  return startSpeechActivityMonitor(readLocalAudioStats, onSpeech, intervalMs);
}
